/**
 * Reactive query layer for SQLite — replaces Dexie's useLiveQuery.
 *
 * Architecture:
 *  - `useSQLiteQuery(queryFn, deps, tables)` subscribes to table change events
 *  - When any write modifies a subscribed table, the query re-runs
 *  - Uses React's useSyncExternalStore for tear-free, immediately-flushed reads
 *
 * Why useSyncExternalStore instead of useState + useEffect:
 *  React 18's scheduler uses MessageChannel to schedule re-renders. Chrome
 *  throttles MessageChannel tasks when the window doesn't have focus, so
 *  setState-based updates triggered from async code (e.g. a background sync)
 *  are delayed until the user re-focuses the tab. useSyncExternalStore forces
 *  React to flush the update synchronously, bypassing the scheduler.
 *
 *  Notifications are wrapped in startTransition so React treats SQLite-driven
 *  re-renders (note list, sidebar counts) as interruptible — urgent updates
 *  like editor input take priority and won't be blocked by list re-renders.
 */

import { useSyncExternalStore, useMemo, useRef, useCallback, startTransition } from 'react'
import { subscribeToChanges, isDbReady } from './sqlite'

/**
 * React hook that re-runs a SQLite query whenever subscribed tables change.
 * Replaces `useLiveQuery` from dexie-react-hooks.
 *
 * SQLite WASM is synchronous and the DB is fully initialized before React
 * renders (main.tsx awaits initDB before createRoot), so queries run inline
 * via useMemo — no async loading window.
 *
 * @param queryFn - Synchronous function that runs SQL queries and returns data
 * @param deps - React dependency array (re-runs query when deps change)
 * @param tables - Table names to subscribe to (re-runs on writes to these tables)
 * @param defaultValue - Fallback if DB is not ready (should never happen in practice)
 */
export function useSQLiteQuery<T>(
  queryFn: () => T,
  deps: unknown[],
  tables: string[],
  defaultValue?: T,
): T | undefined {
  const tablesRef = useRef(tables)
  tablesRef.current = tables
  // Mutable version counter — incremented outside of render, read by getSnapshot
  const versionRef = useRef(0)

  // subscribe must be stable (same reference) to avoid useSyncExternalStore
  // re-subscribing on every render. Captures only refs (always stable).
  const subscribe = useCallback((onStoreChange: () => void) => {
    return subscribeToChanges((changedTables) => {
      if (!isDbReady()) return
      if (tablesRef.current.some((t) => changedTables.has(t))) {
        versionRef.current += 1
        // Mark as a transition so React can interrupt these re-renders
        // to process urgent updates (e.g. editor input). The synchronous
        // getSnapshot read still prevents tearing.
        startTransition(() => onStoreChange())
      }
    })
  }, [])

  const getSnapshot = useCallback(() => versionRef.current, [])

  const version = useSyncExternalStore(subscribe, getSnapshot)

  // Run query synchronously during render — no useEffect loading window
  return useMemo(
    () => {
      if (!isDbReady()) return defaultValue
      try {
        return queryFn()
      } catch {
        return defaultValue
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryFn/defaultValue captured via caller-controlled ...deps spread
    [version, ...deps],
  )
}

/**
 * Hook that triggers a re-render when any of the specified tables change.
 * Useful for components that need to know when data changes but manage
 * their own query logic.
 */
export function useSQLiteSubscription(tables: string[]): number {
  const tablesRef = useRef(tables)
  tablesRef.current = tables
  const versionRef = useRef(0)

  const subscribe = useCallback((onStoreChange: () => void) => {
    return subscribeToChanges((changedTables) => {
      if (tablesRef.current.some((t) => changedTables.has(t))) {
        versionRef.current += 1
        startTransition(() => onStoreChange())
      }
    })
  }, [])

  const getSnapshot = useCallback(() => versionRef.current, [])

  return useSyncExternalStore(subscribe, getSnapshot)
}

/**
 * Fingerprinted reactive query — extracts the common pattern used by hooks
 * that need to skip re-renders when data hasn't meaningfully changed.
 *
 * Instead of re-rendering on every table write, runs a cheap `fingerprint()`
 * query on each change notification. Only when the fingerprint differs does
 * it bump the version and trigger a React re-render, at which point the full
 * `query()` runs via useMemo.
 *
 * Both `fingerprint` and `query` are called from refs so callers don't need
 * to worry about stale closures or memoising their callbacks.
 */
export function useFingerprintedQuery<T>(opts: {
  tables: string[]
  fingerprint: () => string
  query: () => T
  deps?: unknown[]
}): T {
  const optsRef = useRef(opts)
  optsRef.current = opts

  const versionRef = useRef(0)
  const fingerprintRef = useRef<string>('')

  const subscribe = useCallback((onStoreChange: () => void) => {
    return subscribeToChanges((changedTables) => {
      if (!isDbReady()) return
      const { tables, fingerprint } = optsRef.current
      if (!tables.some((t) => changedTables.has(t))) return
      const fp = fingerprint()
      if (fp === fingerprintRef.current) return
      fingerprintRef.current = fp
      versionRef.current += 1
      startTransition(() => onStoreChange())
    })
  }, [])

  const getSnapshot = useCallback(() => versionRef.current, [])
  const version = useSyncExternalStore(subscribe, getSnapshot)

  const deps = opts.deps ?? []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => optsRef.current.query(), [version, ...deps])
}

/**
 * Force a re-query on all subscribers for the given tables.
 * Used after bulk operations like sync or migration.
 */
export { notifyChanges } from './sqlite'
